/**
 * Match objective (AI-07): the estimated chance of finishing the 50 hands with the most points.
 *
 * Model (documented approximation): over the hands still to play each player's points grow by the
 * same average plus independent noise, sd = MATCH_MODEL.sdPerHand / √2 per hand (so the gap between
 * two players has sd `sdPerHand` per hand). Deck links between players are ignored. With no hands
 * left the real final standings decide: points, then states, then seat order (the engine's sort).
 * Other players' futures use fixed quasi-random draws; ours is integrated exactly, which keeps U
 * smooth in our own points.
 */
import { MATCH_MODEL } from "./config.ts";
import { makeRng } from "../rng.ts";

const DRAWS: number[][] = (() => {
  const rng = makeRng(0x5eed);
  const out: number[][] = [];
  for (let i = 0; i < MATCH_MODEL.draws; i++) {
    const row: number[] = [];
    for (let j = 0; j < 5; j++) {
      const u = Math.max(1e-12, rng.next()), v = rng.next();
      row.push(Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v));
    }
    out.push(row);
  }
  return out;
})();

export function normCdf(x: number): number {
  // Abramowitz–Stegun 7.1.26 via erf
  const z = Math.abs(x) / Math.SQRT2, t = 1 / (1 + 0.3275911 * z);
  const erf = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-z * z);
  return x >= 0 ? 0.5 * (1 + erf) : 0.5 * (1 - erf);
}

/** True if seat `a` is ahead of seat `b` in the final standings. */
function ahead(a: number, b: number, pts: number[], states: number[]): boolean {
  if (pts[a] !== pts[b]) return pts[a] > pts[b];
  if (states[a] !== states[b]) return states[a] > states[b];
  return a < b;
}

/** U: probability that `me` wins the match, given everyone's points and states after this hand. */
export function matchWinProb(me: number, pts: number[], states: number[], handsLeft: number): number {
  if (handsLeft <= 0) {
    for (let j = 0; j < 5; j++) if (j !== me && !ahead(me, j, pts, states)) return 0;
    return 1;
  }
  const sd = (MATCH_MODEL.sdPerHand / Math.SQRT2) * Math.sqrt(handsLeft);
  let sum = 0;
  for (const row of DRAWS) {
    let best = -Infinity;
    for (let j = 0; j < 5; j++) if (j !== me) best = Math.max(best, pts[j] + sd * row[j]);
    sum += normCdf((pts[me] - best) / sd);
  }
  return sum / DRAWS.length;
}
